import React, { useEffect, useMemo, useRef, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import {
  Sparkles, Mail, Eye, UserPlus, MessageCircle, Phone, Copy, RefreshCw,
  AlertCircle, Flame, Check, X, ChevronLeft, ChevronRight, Zap, StopCircle, CheckCheck,
} from "lucide-react";
import { toast } from "sonner";

const CHANNEL_ICON = {
  email: Mail,
  linkedin_view: Eye,
  linkedin_connect: UserPlus,
  linkedin_dm: MessageCircle,
  voicemail: Phone,
  task_manual: AlertCircle,
};

const CHANNEL_LABEL = {
  email: "Email",
  linkedin_view: "LinkedIn · view profile",
  linkedin_connect: "LinkedIn · connect request",
  linkedin_dm: "LinkedIn · DM",
  voicemail: "Voicemail drop",
  task_manual: "Manual task",
};

const KIND_LABEL = {
  insight: "Commercial Insight",
  case_study: "Case Study",
  trigger: "Trigger Event",
  meeting_ask: "Meeting Ask",
  follow_up: "Follow-up",
  breakup: "Breakup",
  nurture: "Nurture",
};

const STATUS_BADGE = {
  pending: { label: "Pending", className: "bg-secondary text-muted-foreground" },
  approved: { label: "Approved", className: "bg-success/15 text-success border border-success/30" },
  skipped: { label: "Skipped", className: "bg-muted text-muted-foreground" },
};

export default function BulkPreview() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeLeadId, setActiveLeadId] = useState(null);
  const [previewByLead, setPreviewByLead] = useState({}); // lead_id → { data, draftCount }
  const [job, setJob] = useState(null); // {job_id, status, total, completed, current_lead_id}
  const activeReqRef = useRef(0);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads", { params: { limit: 2000 } });
      setLeads(data);
      if (data.length > 0 && !activeLeadId) {
        setActiveLeadId(data[0].lead_id);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const loadAllSavedPreviews = async (leadList) => {
    // Pull saved previews for every lead in parallel (cheap — just reads DB)
    const results = await Promise.allSettled(
      leadList.map((l) => api.get(`/ai/preview-sequence/${l.lead_id}/saved`).then((r) => ({ leadId: l.lead_id, data: r.data })))
    );
    const map = {};
    results.forEach((r) => {
      if (r.status === "fulfilled" && r.value.data?.has_preview) {
        map[r.value.leadId] = { data: r.value.data };
      }
    });
    setPreviewByLead(map);
  };

  const pollJobStatus = async () => {
    try {
      const { data } = await api.get("/ai/bulk-preview/status");
      const j = data?.job;
      setJob(j);
      // If job advanced, refresh saved previews for the leads it's worked on
      if (j && (j.status === "running" || j.status === "done")) {
        // Pull saved preview for the current lead so the UI fills in progressively
        if (j.current_lead_id) {
          try {
            const { data: p } = await api.get(`/ai/preview-sequence/${j.current_lead_id}/saved`);
            if (p.has_preview) {
              setPreviewByLead((prev) => ({ ...prev, [j.current_lead_id]: { data: p } }));
            }
          } catch {} // eslint-disable-line
        }
        // Also refresh the active lead's preview
        if (activeLeadId && activeLeadId !== j.current_lead_id) {
          try {
            const { data: p } = await api.get(`/ai/preview-sequence/${activeLeadId}/saved`);
            if (p.has_preview) {
              setPreviewByLead((prev) => ({ ...prev, [activeLeadId]: { data: p } }));
            }
          } catch {} // eslint-disable-line
        }
      }
    } catch (err) {
      // ignore poll errors
    }
  };

  useEffect(() => { loadLeads(); }, []); // eslint-disable-line

  // After leads load, fetch their saved previews + start polling
  useEffect(() => {
    if (leads.length === 0) return;
    loadAllSavedPreviews(leads);
    pollJobStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads.length]);

  // Poll every 4s while a job is running
  useEffect(() => {
    if (!job || (job.status !== "running")) return;
    const t = setInterval(pollJobStatus, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.status, activeLeadId]);

  const activeLead = useMemo(
    () => leads.find((l) => l.lead_id === activeLeadId),
    [leads, activeLeadId]
  );
  const activePreview = previewByLead[activeLeadId];

  const counts = useMemo(() => {
    const c = { total: leads.length, pending: 0, approved: 0, skipped: 0, drafted: 0 };
    leads.forEach((l) => {
      const s = l.preview_status || "pending";
      c[s] = (c[s] || 0) + 1;
      const prev = previewByLead[l.lead_id];
      if (prev?.data?.steps) {
        const emailsDone = prev.data.steps.filter((st) => st.channel === "email" && st.subject && !st.error).length;
        if (emailsDone >= 7) c.drafted += 1;
      }
    });
    return c;
  }, [leads, previewByLead]);

  const startBulkJob = async () => {
    try {
      const { data } = await api.post("/ai/bulk-preview/start", {});
      if (data.nothing_to_do) {
        toast.success("All previews already drafted");
        return;
      }
      if (data.already_running) {
        toast.info(`A draft job is already running (${data.completed}/${data.total}) — continuing in the background`);
      } else {
        toast.success(`Drafting ${data.total} sequences in the background. You can leave this page — drafts will keep generating.`);
      }
      pollJobStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start bulk preview");
    }
  };

  const cancelBulkJob = async () => {
    try {
      await api.post("/ai/bulk-preview/cancel");
      toast.success("Stopping after current lead…");
      pollJobStatus();
    } catch (err) {
      toast.error("Cancel failed");
    }
  };

  const bulkReview = async (status, scope, opts = {}) => {
    const verb = status === "approved" ? "Approve" : "Skip";
    const body = { status, scope, ...opts };
    // Friendly preview of count
    const targets = leads.filter((l) => {
      if (scope === "all") return true;
      if (scope === "pending") return !l.preview_status || l.preview_status === "pending";
      if (scope === "drafted") {
        const p = previewByLead[l.lead_id]?.data?.steps || [];
        return p.filter((s) => s.channel === "email" && s.subject && !s.error).length >= 7;
      }
      if (scope === "high_score") return (l.score || 0) >= (opts.min_score || 70);
      return false;
    });
    if (targets.length === 0) {
      toast.info("No leads match this filter");
      return;
    }
    if (!window.confirm(`${verb} ${targets.length} lead${targets.length > 1 ? "s" : ""}? ${status === "approved" ? "(AI will auto-draft sequences for any approved lead that doesn't have drafts yet)" : ""}`)) return;
    try {
      const { data } = await api.post("/leads/bulk-review", body);
      const msg = data.auto_draft_kicked
        ? `${verb}d ${data.updated} lead${data.updated !== 1 ? "s" : ""} · AI drafting ${data.auto_draft_count} sequences in background`
        : `${verb}d ${data.updated} lead${data.updated !== 1 ? "s" : ""}`;
      toast.success(msg);
      // Refresh leads to reflect new statuses
      loadLeads();
      // Refresh job status to show the new background draft job
      if (data.auto_draft_kicked) pollJobStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Bulk ${verb.toLowerCase()} failed`);
    }
  };

  const loadOneSavedPreview = async (leadId) => {
    const reqId = ++activeReqRef.current;
    try {
      const { data } = await api.get(`/ai/preview-sequence/${leadId}/saved`);
      if (activeReqRef.current !== reqId) return;
      if (data.has_preview) {
        setPreviewByLead((prev) => ({ ...prev, [leadId]: { data } }));
      } else {
        // Nothing saved yet — just clear so UI shows "queued / start bulk to draft"
        setPreviewByLead((prev) => {
          const next = { ...prev };
          delete next[leadId];
          return next;
        });
      }
    } catch (err) { /* ignore */ }
  };

  // Refresh active lead's saved preview when it changes
  useEffect(() => {
    if (!activeLeadId) return;
    if (previewByLead[activeLeadId]?.data) return; // already loaded
    loadOneSavedPreview(activeLeadId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLeadId]);

  const setReview = async (leadId, status) => {
    try {
      const { data } = await api.patch(`/leads/${leadId}`, { preview_status: status });
      setLeads((prev) => prev.map((l) => (l.lead_id === leadId ? data : l)));
      toast.success(`Marked ${status}`);
      const idx = leads.findIndex((l) => l.lead_id === leadId);
      const next = leads.slice(idx + 1).find((l) => (l.preview_status || "pending") === "pending");
      if (next) setActiveLeadId(next.lead_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const goPrev = () => {
    const idx = leads.findIndex((l) => l.lead_id === activeLeadId);
    if (idx > 0) setActiveLeadId(leads[idx - 1].lead_id);
  };
  const goNext = () => {
    const idx = leads.findIndex((l) => l.lead_id === activeLeadId);
    if (idx >= 0 && idx < leads.length - 1) setActiveLeadId(leads[idx + 1].lead_id);
  };

  const copyStep = async (step) => {
    try {
      const text = step.channel === "email"
        ? `Subject: ${step.subject || ""}\n\n${step.body_text || ""}`
        : step.task_note || "";
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch { toast.error("Copy failed"); }
  };

  const regenerateOne = async (order) => {
    if (!activeLeadId) return;
    try {
      const { data: stepData } = await api.post(
        `/ai/preview-sequence/${activeLeadId}/step/${order}`,
        null,
        { timeout: 60000 }
      );
      setPreviewByLead((prev) => {
        const cur = prev[activeLeadId];
        if (!cur?.data) return prev;
        return {
          ...prev,
          [activeLeadId]: {
            ...cur,
            data: {
              ...cur.data,
              steps: cur.data.steps.map((st) =>
                st.order === order
                  ? { ...st, subject: stepData.subject, body_text: stepData.body_text, error: stepData.error }
                  : st
              ),
            },
          },
        };
      });
      toast.success("Re-drafted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Regenerate failed");
    }
  };

  const emailTotal = activePreview?.data ? activePreview.data.steps.filter((s) => s.channel === "email").length : 0;
  const emailDone = activePreview?.data ? activePreview.data.steps.filter((s) => s.channel === "email" && s.subject && !s.error).length : 0;
  const jobRunning = job?.status === "running";

  // Detect LLM budget exhaustion from job errors so we can show an actionable banner
  const budgetError = (job?.errors || []).some((e) => e?.reason === "budget");
  const errorCount = (job?.errors || []).length;

  return (
    <Layout
      title="Bulk Preview"
      subtitle="Review every AI-drafted sequence before Go-Live · approve or skip per lead"
      actions={
        <>
          {jobRunning ? (
            <Button variant="outline" onClick={cancelBulkJob} data-testid="bulk-cancel" style={{ borderRadius: 2 }}>
              <StopCircle className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Stop drafting
            </Button>
          ) : (
            <Button variant="outline" onClick={startBulkJob} disabled={leads.length === 0} data-testid="bulk-generate-all" style={{ borderRadius: 2 }}>
              <Zap className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Generate all
            </Button>
          )}
        </>
      }
    >
      {/* Summary strip */}
      <div className="flex items-center gap-6 mb-4 text-sm flex-wrap" data-testid="bulk-summary">
        <span className="overline">Total <strong className="text-foreground ml-1 text-sm">{counts.total}</strong></span>
        <span className="overline">Approved <strong className="text-success ml-1 text-sm" data-testid="count-approved">{counts.approved}</strong></span>
        <span className="overline">Pending <strong className="text-foreground ml-1 text-sm">{counts.pending}</strong></span>
        <span className="overline">Skipped <strong className="text-muted-foreground ml-1 text-sm">{counts.skipped}</strong></span>
        <span className="overline">Previews drafted <strong className="text-foreground ml-1 text-sm">{counts.drafted}/{counts.total}</strong></span>
      </div>

      {/* Bulk action bar — one-click approve/skip many leads */}
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="bulk-actions">
        <span className="text-xs uppercase tracking-widest text-muted-foreground font-mono mr-1">Bulk:</span>
        <Button
          size="sm"
          onClick={() => bulkReview("approved", "all")}
          data-testid="bulk-approve-all"
          style={{ borderRadius: 2 }}
          title="Approves all leads AND auto-drafts AI sequences for any that aren't drafted yet. One click = done."
        >
          <Zap className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Approve all & draft ({counts.total})
        </Button>
        <Button
          size="sm" variant="outline"
          onClick={() => bulkReview("approved", "high_score", { min_score: 70 })}
          data-testid="bulk-approve-high-score"
          style={{ borderRadius: 2 }}
          title="Approve every lead with AI score ≥ 70 + auto-draft their sequences"
        >
          <CheckCheck className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Approve score ≥ 70
        </Button>
        <Button
          size="sm" variant="outline"
          onClick={() => bulkReview("approved", "drafted")}
          disabled={counts.drafted === 0}
          data-testid="bulk-approve-drafted"
          style={{ borderRadius: 2 }}
          title="Approve only leads that already have 7 drafts ready (no new LLM cost)"
        >
          <Check className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Approve drafted-only ({counts.drafted})
        </Button>
        <Button
          size="sm" variant="ghost"
          onClick={() => bulkReview("skipped", "pending")}
          disabled={counts.pending === 0}
          data-testid="bulk-skip-pending"
          style={{ borderRadius: 2 }}
          title="Skip every lead that hasn't been reviewed yet"
        >
          <X className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Skip pending ({counts.pending})
        </Button>
      </div>

      {/* LLM Budget reminder (always visible) */}
      <div className="mb-4 flex items-start gap-3 px-4 py-2.5 bg-secondary/30 border border-border text-xs" style={{ borderRadius: 2 }} data-testid="bulk-llm-budget-hint">
        <AlertCircle className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" strokeWidth={1.5} />
        <div className="leading-relaxed">
          <span className="font-medium">AI cost: ~$8-12 for 53 leads</span> (≈ 370 LLM calls).
          Check your Universal Key balance before clicking "Generate all" or "Approve all & draft" — top up at <strong>Profile → Universal Key → Add Balance</strong> if low.
        </div>
      </div>

      {/* Budget-exhaustion error banner (auto-detected from failed drafts) */}
      {budgetError && (
        <div className="mb-4 flex items-start gap-3 px-4 py-3 bg-destructive/10 border border-destructive/30" style={{ borderRadius: 2 }} data-testid="bulk-budget-error">
          <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" strokeWidth={1.5} />
          <div className="text-sm leading-relaxed">
            <div className="font-medium text-destructive">LLM credit balance exhausted — drafts are failing</div>
            <div className="text-foreground/80 mt-1">
              Your Emergent Universal Key has run out of credits. {errorCount} draft{errorCount > 1 ? "s" : ""} failed.
              Top up at <strong>Profile → Universal Key → Add Balance</strong>, then click <strong>Generate all</strong> again — only the failed leads will be re-drafted.
            </div>
          </div>
        </div>
      )}
      {/* Background job status banner */}
      {jobRunning && (
        <div className="mb-4 flex items-center gap-3 px-4 py-2.5 bg-secondary/40 border border-border" style={{ borderRadius: 2 }} data-testid="bulk-job-banner">
          <RefreshCw className="h-3.5 w-3.5 animate-spin text-muted-foreground" strokeWidth={1.5} />
          <div className="text-xs">
            <span className="font-medium">Drafting in background</span>
            <span className="text-muted-foreground ml-2">{job.completed}/{job.total} leads complete · safe to leave this page · drafts auto-save</span>
          </div>
        </div>
      )}
      {job?.status === "done" && job.completed === job.total && counts.drafted === counts.total && (
        <div className="mb-4 flex items-center gap-3 px-4 py-2.5 bg-success/10 border border-success/30" style={{ borderRadius: 2 }}>
          <Check className="h-3.5 w-3.5 text-success" strokeWidth={1.5} />
          <div className="text-xs">All {job.total} sequences drafted. You can review and approve now.</div>
        </div>
      )}

      <div className="grid grid-cols-12 gap-6">
        {/* Left sidebar — lead list */}
        <aside className="col-span-12 lg:col-span-3 bg-card border border-border max-h-[calc(100vh-260px)] overflow-y-auto" style={{ borderRadius: 2 }} data-testid="bulk-lead-list">
          {loading && <div className="px-6 py-8 text-xs text-muted-foreground">Loading…</div>}
          {!loading && leads.length === 0 && (
            <div className="px-6 py-8 text-xs text-muted-foreground">No leads. Import some on the Leads page.</div>
          )}
          {leads.map((l) => {
            const status = l.preview_status || "pending";
            const badge = STATUS_BADGE[status];
            const isActive = l.lead_id === activeLeadId;
            const previewState = previewByLead[l.lead_id];
            const stepsArr = previewState?.data?.steps;
            const emailsDoneCount = stepsArr ? stepsArr.filter((s) => s.channel === "email" && s.subject && !s.error).length : 0;
            const drafted = emailsDoneCount >= 7;
            const partial = emailsDoneCount > 0 && emailsDoneCount < 7;
            const isCurrent = job?.current_lead_id === l.lead_id && jobRunning;
            return (
              <button
                key={l.lead_id}
                onClick={() => setActiveLeadId(l.lead_id)}
                className={`w-full text-left px-4 py-3 border-b border-border transition-colors ${isActive ? "bg-secondary/60" : "hover:bg-secondary/30"}`}
                data-testid={`bulk-lead-${l.lead_id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{l.full_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{l.title} · {l.company}</div>
                  </div>
                  <span className={`text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 ${badge.className}`} style={{ borderRadius: 2 }}>
                    {badge.label}
                  </span>
                </div>
                <div className="mt-1.5 flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground font-mono">
                    {isCurrent ? "Drafting now…" : drafted ? "Drafted 7/7" : partial ? `Drafted ${emailsDoneCount}/7` : "Not drafted"}
                  </span>
                  {drafted && <span className="text-[10px] text-muted-foreground font-mono">· score {l.score}</span>}
                </div>
              </button>
            );
          })}
        </aside>

        {/* Right panel — preview */}
        <section className="col-span-12 lg:col-span-9 bg-card border border-border p-6 max-h-[calc(100vh-260px)] overflow-y-auto" style={{ borderRadius: 2 }} data-testid="bulk-preview-panel">
          {!activeLead && (
            <div className="text-sm text-muted-foreground">Pick a lead from the list to preview.</div>
          )}
          {activeLead && (
            <>
              {/* Header */}
              <div className="flex items-start justify-between gap-4 mb-4 pb-4 border-b border-border flex-wrap">
                <div className="min-w-0">
                  <div className="overline mb-1">{activeLead.industry}</div>
                  <div className="font-serif text-2xl tracking-tight" style={{ fontWeight: 400 }}>{activeLead.full_name}</div>
                  <div className="text-sm text-muted-foreground truncate">{activeLead.title} · {activeLead.company}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" variant="ghost" onClick={goPrev} data-testid="bulk-prev" style={{ borderRadius: 2 }}>
                    <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> Prev
                  </Button>
                  <Button size="sm" variant="ghost" onClick={goNext} data-testid="bulk-next" style={{ borderRadius: 2 }}>
                    Next <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setReview(activeLead.lead_id, "skipped")} data-testid="bulk-skip" style={{ borderRadius: 2 }}>
                    <X className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> Skip
                  </Button>
                  <Button size="sm" onClick={() => setReview(activeLead.lead_id, "approved")} data-testid="bulk-approve" style={{ borderRadius: 2 }}>
                    <Check className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> Approve
                  </Button>
                </div>
              </div>

              {/* No preview yet */}
              {!activePreview?.data && (
                <div className="border border-dashed border-border p-8 text-center" style={{ borderRadius: 2 }}>
                  <Sparkles className="h-6 w-6 mx-auto mb-3 text-muted-foreground" strokeWidth={1.5} />
                  <p className="text-sm text-muted-foreground mb-3">
                    No draft yet for this lead. {jobRunning ? "The background job will reach it soon." : "Click \"Generate all\" to draft sequences for every lead."}
                  </p>
                  {!jobRunning && (
                    <Button size="sm" onClick={startBulkJob} style={{ borderRadius: 2 }}>
                      <Zap className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Generate all
                    </Button>
                  )}
                </div>
              )}

              {activePreview?.data && (
                <>
                  <div className="bg-secondary/30 border border-border p-4 space-y-2 mb-5" style={{ borderRadius: 2 }} data-testid="bulk-research-seed">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="overline">AI research seed</div>
                      <div className="flex items-center gap-3">
                        {emailTotal > 0 && (
                          <span className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">
                            {emailDone}/{emailTotal} emails drafted{emailDone === emailTotal ? " · done" : "…"}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      <div><span className="text-xs uppercase tracking-widest text-muted-foreground font-mono">Trigger:</span> {activePreview.data.research?.trigger_event || "—"}</div>
                      <div><span className="text-xs uppercase tracking-widest text-muted-foreground font-mono">Angle:</span> {activePreview.data.research?.outreach_angle || "—"}</div>
                      <div className="md:col-span-2"><span className="text-xs uppercase tracking-widest text-muted-foreground font-mono">Pain points:</span> {(activePreview.data.research?.pain_points || []).join(" · ") || "—"}</div>
                    </div>
                  </div>

                  <div className="space-y-3" data-testid="bulk-steps">
                    {activePreview.data.steps.map((step) => {
                      const Icon = CHANNEL_ICON[step.channel] || Mail;
                      const isEmail = step.channel === "email";
                      const drafted = isEmail && step.subject && !step.error;
                      return (
                        <div
                          key={step.order}
                          className={`border ${isEmail ? "border-border" : "border-dashed border-border"} bg-background p-4`}
                          style={{ borderRadius: 2 }}
                          data-testid={`bulk-step-${step.order}`}
                        >
                          <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 bg-foreground text-background" style={{ borderRadius: 2 }}>
                                Step {step.order}
                              </span>
                              <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                              <span className="text-xs uppercase tracking-widest text-muted-foreground font-mono">
                                {CHANNEL_LABEL[step.channel] || step.channel}
                              </span>
                              {isEmail && KIND_LABEL[step.kind] && (
                                <span className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 bg-secondary text-foreground" style={{ borderRadius: 2 }}>
                                  {KIND_LABEL[step.kind]}
                                </span>
                              )}
                              {step.kind === "trigger" && <Flame className="h-3 w-3 text-primary" strokeWidth={1.5} />}
                              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Day +{step.cumulative_days}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              {isEmail && drafted && (
                                <button
                                  onClick={() => regenerateOne(step.order)}
                                  className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                                >
                                  <RefreshCw className="h-3 w-3" strokeWidth={1.5} /> Re-draft
                                </button>
                              )}
                              <button onClick={() => copyStep(step)} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                                <Copy className="h-3 w-3" strokeWidth={1.5} /> Copy
                              </button>
                            </div>
                          </div>

                          {isEmail ? (
                            <>
                              {step.error ? (
                                <div className="text-xs text-destructive flex items-center gap-1">
                                  <AlertCircle className="h-3 w-3" strokeWidth={1.5} /> {step.body_text}
                                </div>
                              ) : step.subject ? (
                                <>
                                  <div className="text-sm font-medium mb-1.5">{step.subject}</div>
                                  <pre className="whitespace-pre-wrap text-sm text-foreground/90 font-sans leading-relaxed">{step.body_text}</pre>
                                </>
                              ) : (
                                <div className="text-xs text-muted-foreground italic py-2">Not drafted yet — {jobRunning ? "background job in progress" : "click Generate all"}</div>
                              )}
                            </>
                          ) : (
                            <div className="text-sm italic text-foreground/90 leading-relaxed">{step.task_note || step.subject || "(no template)"}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </section>
      </div>
    </Layout>
  );
}
